import type { components, paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const OPEN_POSITION_RESOURCE = "open-position" as const;
export const OPEN_POSITION_SEARCH_OPERATION = "search" as const;
export const OPEN_POSITION_SEARCH_API_PATH = `/v1/${OPEN_POSITION_RESOURCE}/${OPEN_POSITION_SEARCH_OPERATION}` as const;

export type OpenPosition = components["schemas"]["OpenPosition"];
export type OpenPositionSearchParameters = paths[typeof OPEN_POSITION_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type OpenPositionSearchBody =
  paths[typeof OPEN_POSITION_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type OpenPositionSearchResponse =
  paths[typeof OPEN_POSITION_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useOpenPositionSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<OpenPositionSearchParameters, OpenPositionSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    OpenPositionSearchResponse,
    Error,
    OpenPositionSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<OpenPositionSearchParameters, OpenPositionSearchBody>>(options);
  const params = handlePaginatedParameters(optionsWithDefaults.parameters);
  const body = optionsWithDefaults.body;

  return useQuery({
    queryKey: [OPEN_POSITION_RESOURCE, OPEN_POSITION_SEARCH_OPERATION, { params, body }],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<OpenPositionSearchResponse>(OPEN_POSITION_SEARCH_API_PATH, body, {
        params,
      });

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
